import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {MiniDocRowSelection} from '../../components/document-viewer/doc-list/DocViewDocListDataGrid';
import {ToastType} from '../../shared/constants';
import {
  DocViewResponse,
  IDocumentViewerCodingFormListResponse,
  IThreadDocuments,
} from '../../api/types/document.type';

export type CodeSameAsToast = {
  showToast: boolean;
  toastMessage: string;
  toastType: ToastType;
};
export type Trigger = {isTriggered: boolean};
export type Panels = {
  leftPanels: string[];
  rightPanels: string[];
  popoutsOpen: string[];
  fixedPanels: boolean;
};
interface DocViewState {
  documentListMasterId: string;
  sourceTypeCode: string;
  navigationSourceTypeCode: string;
  assignmentSource: string;
  sourceId: number;
  activeDocumentId: string;
  activeDoc: MiniDocRowSelection | null;
  nextDoc: MiniDocRowSelection | null;
  documentHistory: MiniDocRowSelection[];
  selectedDocIds: string[];
  checkedDocs: MiniDocRowSelection[];
  codeSameAsDocs: MiniDocRowSelection[];
  codeSameAsTriggerState: Trigger;
  removeCodeSameAsTriggerState: Trigger;
  codeSameAsToastState: CodeSameAsToast;
  codingFormToastState: CodeSameAsToast;
  docViewConfiguration: DocViewResponse;
  docViewLoaded: boolean;
  reductionPropagation?: boolean;
  totalDocsCount: number;
  codingFormList: IDocumentViewerCodingFormListResponse[];
  isPinCodingFormSelected: boolean;
  isPopoutOpen: boolean;
  panels: Panels;
  docAnalytics: {
    checkedDocsThreadMap: IThreadDocuments[];
    checkedDocsFamilymembers: MiniDocRowSelection[];
    checkedDocsNearDupe: MiniDocRowSelection[];
    checkedDocsConceptuallySimilar: MiniDocRowSelection[];
    currentTab: string;
    previouslySelectedDocId: string;
  };
  showMediaReviewerRemarksPanel: boolean;
  saveCodingFormResponse: {
    jsonData: number;
    lstDocList: string[];
    xmlCompletedDocumentEditedCodingForm: string;
  };
  completedDocs: MiniDocRowSelection[];
  isDocumentCompleted: boolean;
  assignmentMode: boolean;
  miniDocListRefreshKey: number;
  docAnalyticsRefreshKey: number;
  shouldRefreshAssignmentProgress: number;
  pendingDocumentNavigation: any | null;
  assignmentProgressEnabled: boolean;
  warningToastState: CodeSameAsToast;
  codeSameAsDocumentCodingForm: any[];
  isCodeSameAsPrevious: boolean;
  showCompletedDocs: boolean;
}

export const defaultSaveCodingFormResponse = {
  jsonData: 0,
  lstDocList: [],
  xmlCompletedDocumentEditedCodingForm: '',
};

const initialState: DocViewState = {
  documentListMasterId: '',
  sourceTypeCode: '',
  navigationSourceTypeCode: '',
  activeDocumentId: '',
  activeDoc: null,
  nextDoc: null,
  documentHistory: [],
  selectedDocIds: [],
  checkedDocs: [],
  codeSameAsDocs: [],
  codeSameAsTriggerState: {isTriggered: false},
  removeCodeSameAsTriggerState: {isTriggered: false},
  codeSameAsToastState: {
    showToast: false,
    toastMessage: '',
    toastType: ToastType.Success,
  },
  codingFormToastState: {
    showToast: false,
    toastMessage: '',
    toastType: ToastType.Success,
  },
  docViewConfiguration: {
    miniDocListView: true,
    isShowFoldersTab: true,
    assignmentProgress: true,
    analyticsPanelView: true,
    isShowEmailThreadMapTabAnalyticsPanel: true,
    isShowNearDuplicateTabAnalyticsPanel: true,
  } as DocViewResponse,
  docViewLoaded: false,
  reductionPropagation: false,
  totalDocsCount: 0,
  codingFormList: [],
  isPinCodingFormSelected: false,
  isPopoutOpen: false,
  panels: {
    leftPanels: [],
    rightPanels: [],
    popoutsOpen: [],
    fixedPanels: false,
  },
  docAnalytics: {
    checkedDocsThreadMap: [],
    checkedDocsFamilymembers: [],
    checkedDocsNearDupe: [],
    checkedDocsConceptuallySimilar: [],
    currentTab: '',
    previouslySelectedDocId: '',
  },
  showMediaReviewerRemarksPanel: false,
  saveCodingFormResponse: defaultSaveCodingFormResponse,
  assignmentSource: '',
  sourceId: 0,
  completedDocs: [],
  isDocumentCompleted: false,
  assignmentMode: false,
  miniDocListRefreshKey: 0,
  docAnalyticsRefreshKey: 0,
  shouldRefreshAssignmentProgress: 0,
  pendingDocumentNavigation: null,
  assignmentProgressEnabled: false,
  warningToastState: {
    showToast: false,
    toastMessage: '',
    toastType: ToastType.Warning,
  },
  codeSameAsDocumentCodingForm: [],
  isCodeSameAsPrevious: false,
  showCompletedDocs: false,
};

const MAX_HISTORY_LENGTH = 10;
const docViewSlice = createSlice({
  name: 'docView',
  initialState,
  reducers: {
    setDocViewLoaded(state, action: PayloadAction<boolean>) {
      state.docViewLoaded = action.payload;
    },
    storeActiveDocumentId(state, action: PayloadAction<string>) {
      state.activeDocumentId = action.payload;
    },
    setActiveDoc(state, action: PayloadAction<MiniDocRowSelection | null>) {
      state.activeDoc = action.payload;
    },
    setNextDoc(state, action: PayloadAction<MiniDocRowSelection | null>) {
      state.nextDoc = action.payload;
    },
    storeDocumentListMasterId(state, action: PayloadAction<string>) {
      state.documentListMasterId = action.payload;
    },
    storeSourceTypeCode(state, action: PayloadAction<string>) {
      state.sourceTypeCode = action.payload;
    },
    storeNavigationSourceTypeCode(state, action: PayloadAction<string>) {
      state.navigationSourceTypeCode = action.payload;
    },
    storeAssignmentSource(state, action: PayloadAction<string>) {
      state.assignmentSource = action.payload;
    },
    storeSourceId(state, action: PayloadAction<number>) {
      state.sourceId = action.payload;
    },
    storeDocViewConfiguration(state, action: PayloadAction<DocViewResponse>) {
      state.docViewConfiguration = action.payload;
    },

    clearDocumentHistory: (state) => {
      state.documentHistory = initialState.documentHistory;
    },

    storeDocumentIdInHistory: (
      state,
      action: PayloadAction<MiniDocRowSelection>
    ) => {
      const newDoc = action.payload;

      const exists = state.documentHistory.some(
        (doc) => doc.docId === newDoc.docId
      );
      if (exists) return;

      if (state.documentHistory.length >= MAX_HISTORY_LENGTH) {
        state.documentHistory.pop(); // remove last (oldest)
      }

      state.documentHistory.unshift(newDoc);
    },
    setCheckedDocs(state, action: PayloadAction<MiniDocRowSelection[]>) {
      state.checkedDocs = action.payload;
    },
    setCodeSameAsDocs(state, action: PayloadAction<MiniDocRowSelection[]>) {
      state.codeSameAsDocs = [
        ...state.codeSameAsDocs,
        ...action.payload.filter(
          (payloaDoc) =>
            !state.codeSameAsDocs.some(
              (doc) => doc.documentId == payloaDoc.documentId
            )
        ),
      ];
    },
    removeCodeSameAsDocs(state, action: PayloadAction<MiniDocRowSelection[]>) {
      // null/undefined check for testcases
      if (!state.codeSameAsDocs) {
        state.codeSameAsDocs = [];
        return;
      }

      state.codeSameAsDocs = state.codeSameAsDocs.filter(
        (doc) =>
          !action.payload.some(
            (payloadDoc) => payloadDoc.documentId == doc.documentId
          )
      );
    },
    triggerCodeSameAs(state, action: PayloadAction<Trigger>) {
      state.codeSameAsTriggerState = action.payload;
    },
    triggerRemoveCodeSameAs(state, action: PayloadAction<Trigger>) {
      state.removeCodeSameAsTriggerState = action.payload;
    },
    clearCheckedDocsId(state) {
      state.checkedDocs = initialState.checkedDocs;
    },
    setAssignmentProgressEnabled(state, action: PayloadAction<boolean>) {
      state.assignmentProgressEnabled = action.payload;
    },
    setWarningToastState(state, action: PayloadAction<CodeSameAsToast>) {
      state.warningToastState = action.payload;
    },
    clearWarningToastState(state) {
      state.warningToastState = initialState.warningToastState;
    },
    clearCodeSameAsDocsId(state) {
      state.codeSameAsDocs = initialState.codeSameAsDocs;
    },
    clearState(state) {
      state.selectedDocIds = initialState.selectedDocIds;

      state.codeSameAsDocs = initialState.codeSameAsDocs;
      state.checkedDocs = initialState.checkedDocs;
      state.activeDocumentId = initialState.activeDocumentId;
      state.documentHistory = initialState.documentHistory;
      state.documentListMasterId = initialState.documentListMasterId;
      state.sourceTypeCode = initialState.sourceTypeCode;
      state.docViewConfiguration = initialState.docViewConfiguration;
      state.codeSameAsTriggerState = initialState.codeSameAsTriggerState;
      state.removeCodeSameAsTriggerState =
        initialState.removeCodeSameAsTriggerState;
      state.activeDoc = initialState.activeDoc;
      state.docViewLoaded = initialState.docViewLoaded;
      state.reductionPropagation = initialState.reductionPropagation;
      state.docAnalytics = initialState.docAnalytics;
      state.assignmentSource = initialState.assignmentSource;
      state.sourceId = initialState.sourceId;
      state.completedDocs = initialState.completedDocs;
      state.isDocumentCompleted = initialState.isDocumentCompleted;
      state.assignmentMode = initialState.assignmentMode;
      state.miniDocListRefreshKey = initialState.miniDocListRefreshKey;

      state.warningToastState = initialState.warningToastState;
    },
    setCodeSameAsToastState(state, action: PayloadAction<CodeSameAsToast>) {
      state.codeSameAsToastState = action.payload;
    },
    clearCodeSameAsToastState(state) {
      state.codeSameAsToastState = initialState.codeSameAsToastState;
    },
    setCodingFormToastState(state, action: PayloadAction<CodeSameAsToast>) {
      state.codingFormToastState = action.payload;
    },
    clearCodingFormToastState(state) {
      state.codingFormToastState = initialState.codingFormToastState;
    },
    setTotalDocsCount(state, action: PayloadAction<number>) {
      state.totalDocsCount = action.payload;
    },
    setReductionPropagation(state, action: PayloadAction<boolean>) {
      state.reductionPropagation = action.payload;
    },
    setCodingFormList(
      state,
      action: PayloadAction<IDocumentViewerCodingFormListResponse[]>
    ) {
      state.codingFormList = action.payload;
    },
    setIsPinCodingFormSelected(state, action: PayloadAction<boolean>) {
      state.isPinCodingFormSelected = action.payload;
    },
    setDocAnalyticsCurrentTab(state, action: PayloadAction<string>) {
      state.docAnalytics.currentTab = action.payload;
    },
    setDocAnalyticsCheckedDocsCurrentTab(state, action: PayloadAction<string>) {
      state.docAnalytics.currentTab = action.payload;
    },
    setDocAnalyticsCheckedDocsThreadMap(
      state,
      action: PayloadAction<IThreadDocuments[]>
    ) {
      state.docAnalytics.checkedDocsThreadMap = action.payload;
    },
    setDocAnalyticsCheckedDocsFamilyMembers(
      state,
      action: PayloadAction<MiniDocRowSelection[]>
    ) {
      state.docAnalytics.checkedDocsFamilymembers = action.payload;
    },
    setDocAnalyticsCheckedDocsNearDupe(
      state,
      action: PayloadAction<MiniDocRowSelection[]>
    ) {
      state.docAnalytics.checkedDocsNearDupe = action.payload;
    },
    setDocAnalyticsCheckedDocsConceptuallySimilar(
      state,
      action: PayloadAction<MiniDocRowSelection[]>
    ) {
      state.docAnalytics.checkedDocsConceptuallySimilar = action.payload;
    },
    setPopoutState(state, action: PayloadAction<boolean>) {
      state.isPopoutOpen = action.payload;
    },
    setDocAnalyticsPreviousId(state, action: PayloadAction<string>) {
      state.docAnalytics.previouslySelectedDocId = action.payload;
    },
    setDocMediaReviewerRemarksPanel(state, action: PayloadAction<boolean>) {
      state.showMediaReviewerRemarksPanel = action.payload;
    },
    setSaveCodingFormResponse(state, action: PayloadAction<any>) {
      state.saveCodingFormResponse = action.payload;
    },
    setAssignmentMode(state, action: PayloadAction<boolean>) {
      state.assignmentMode = action.payload;
    },
    setIsDocumentCompleted(state, action: PayloadAction<boolean>) {
      state.isDocumentCompleted = action.payload;
    },
    triggerAssignmentProgressRefresh: (state) => {
      state.shouldRefreshAssignmentProgress =
        (state.shouldRefreshAssignmentProgress || 0) + 1;
    },
    incrementDocAnalyticsRefreshKey: (state) => {
      state.docAnalyticsRefreshKey = (state.docAnalyticsRefreshKey || 0) + 1;
    },
    incrementMiniDocListRefreshKey: (state) => {
      state.miniDocListRefreshKey = (state.miniDocListRefreshKey || 0) + 1;
    },
    setCompletedDocs(state, action: PayloadAction<MiniDocRowSelection[]>) {
      state.completedDocs = [
        ...state.completedDocs,
        ...action.payload.filter(
          (payloadDoc) =>
            !state.completedDocs.some(
              (doc) => doc.documentId == payloadDoc.documentId
            )
        ),
      ];
    },
    removeCompletedDocs(state, action: PayloadAction<MiniDocRowSelection[]>) {
      state.completedDocs = state.completedDocs.filter(
        (doc) =>
          !action.payload.some(
            (payloadDoc) => payloadDoc.documentId == doc.documentId
          )
      );
    },
    clearCompletedDocsId(state) {
      state.completedDocs = initialState.completedDocs;
    },
    setPendingDocumentNavigation: (
      state,
      action: PayloadAction<any | null>
    ) => {
      state.pendingDocumentNavigation = action.payload;
    },
    setPanels(state, action: PayloadAction<Panels>) {
      state.panels = action.payload;
    },
    setCodeSameAsDocumentCodingForm(state, action: PayloadAction<any>) {
      state.codeSameAsDocumentCodingForm = action.payload;
    },
    setIsCodeSameAsPrevious(state, action: PayloadAction<any>) {
      state.isCodeSameAsPrevious = action.payload;
    },
    setShowCompletedDocs(state, action: PayloadAction<any>) {
      state.showCompletedDocs = action.payload;
    },
  },
});
export const docViewInitialState = initialState;

export const {
  setCheckedDocs,
  setCodeSameAsDocs,
  removeCodeSameAsDocs,
  clearCheckedDocsId,
  clearCodeSameAsDocsId,
  triggerCodeSameAs,
  triggerRemoveCodeSameAs,
  storeActiveDocumentId,
  clearState,
  storeDocumentIdInHistory,
  clearDocumentHistory,
  storeDocumentListMasterId,
  storeSourceTypeCode,
  storeNavigationSourceTypeCode,
  storeDocViewConfiguration,
  setCodeSameAsToastState,
  clearCodeSameAsToastState,
  setCodingFormToastState,
  clearCodingFormToastState,
  setActiveDoc,
  setDocViewLoaded,
  setReductionPropagation,
  setTotalDocsCount,
  setCodingFormList,
  setIsPinCodingFormSelected,
  setDocAnalyticsCurrentTab,
  setPopoutState,
  setDocAnalyticsCheckedDocsCurrentTab,
  setDocAnalyticsCheckedDocsThreadMap,
  setDocAnalyticsCheckedDocsFamilyMembers,
  setDocAnalyticsCheckedDocsNearDupe,
  setDocAnalyticsCheckedDocsConceptuallySimilar,
  setDocAnalyticsPreviousId,
  setDocMediaReviewerRemarksPanel,
  setSaveCodingFormResponse,
  storeAssignmentSource,
  storeSourceId,
  setCompletedDocs,
  removeCompletedDocs,
  clearCompletedDocsId,
  incrementDocAnalyticsRefreshKey,
  incrementMiniDocListRefreshKey,
  setAssignmentMode,
  setIsDocumentCompleted,
  triggerAssignmentProgressRefresh,
  setPendingDocumentNavigation,
  setPanels,
  setAssignmentProgressEnabled,
  setWarningToastState,
  clearWarningToastState,
  setCodeSameAsDocumentCodingForm,
  setIsCodeSameAsPrevious,
  setShowCompletedDocs,
  setNextDoc,
} = docViewSlice.actions;

export default docViewSlice.reducer;
